import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Heart, Sparkles, ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';

// Five real-style examples covering different life situations + personas.
// Every AI response was crafted to match the persona's voice from prompts.py.
const EXAMPLES = [
  {
    persona: 'Dad',
    user: "I'm just tired. I work full time, I'm trying to be a good dad, and tonight my kid told me he hates me. I know he doesn't mean it but I can't stop replaying it. I feel like I'm failing at everything.",
    ai: "Hey bud. Take a breath with me for a second. Your kid screaming \"I hate you\" — that one stings deep, doesn't it? Especially when you're already running on fumes. Listen to me: a kid who feels safe enough to throw those words at you is a kid who knows you're not going anywhere. That's not failing. That's the opposite. You're tired because you're carrying a lot. That's not weakness — that's the cost of caring this hard. Tonight, you don't have to fix anything. Just get yourself to bed. Tomorrow, you go give that kid a hug. He needs it. So do you.",
    tones: ['exhaustion', 'self-criticism', 'needs rest', 'needs permission'],
  },
  {
    persona: 'Big Sis',
    user: "I just found out my best friend has been talking about me behind my back. Like, for months. I don't even know who I can trust anymore. I feel so stupid for not seeing it.",
    ai: "Okay babe, hold on. First of all — you're NOT stupid. You trusted someone who was supposed to be trustworthy. That's on her, not you. The fact that you couldn't see it just means you don't operate that way. Take a second to feel that punch though, because it IS a punch. Months of fake smiles? That hurts. You don't have to figure out who you trust today. You just have to give yourself permission to feel betrayed without rushing to be \"over it.\" What's actually going through your head right now, sis? Just lay it out.",
    tones: ['betrayal', 'self-blame', 'shaken trust'],
  },
  {
    persona: 'Coach',
    user: "I've been trying to get back into the gym for months. I sign up, miss a week, give up, repeat. I'm starting to think I just don't have it in me anymore.",
    ai: "Hold up, champ. \"Don't have it in me\" — that's a story, not a fact. You know what I'm hearing? You've tried to get back in the gym FOUR times. Most people stop after one. The pattern isn't \"giving up\" — it's \"you keep coming back.\" That's the muscle that matters. Here's the deal: stop trying to be the version of you that goes 5 days a week. That guy doesn't exist yet. Be the guy who goes once this week. ONCE. Then we'll talk. What's the smallest version of a workout you could not say no to tomorrow?",
    tones: ['self-doubt', 'frustration', 'needs reframe'],
  },
  {
    persona: 'The Listener',
    user: "My mom passed two months ago. Everyone keeps asking how I'm doing but I don't know what to say. Some days I'm fine. Some days I can't get out of bed. I don't think I'm grieving right.",
    ai: "There's no right way. There just isn't. The fact that some days feel okay doesn't mean you've moved on. The fact that some days you can't move doesn't mean you're broken. Grief comes in waves — it doesn't read a calendar. Two months is nothing for losing your mom. Take your time. You don't owe anyone a tidy answer when they ask how you're doing. \"Today is hard\" is a complete sentence.",
    tones: ['grief', 'self-doubt', 'needs permission'],
  },
  {
    persona: 'The Real One',
    user: "Everyone around me is moving forward — buying houses, having kids, getting promoted. I feel like I'm just stuck watching from the sidelines. What's wrong with me?",
    ai: "Friend, because I care about you, I'm gonna shoot straight: nothing is wrong with you. That highlight reel you're watching? It's everyone else's edited version of their life. Nobody's posting the panic attacks, the credit card debt, the marriage problems, the 3am stares at the ceiling. You're comparing your raw footage to their movie trailer. That's a rigged game. Your timeline is your timeline. Some plants flower in spring, some bloom in the fall. What does YOUR version of moving forward actually look like? Not theirs. Yours.",
    tones: ['comparison', 'self-doubt', 'hustle-shame'],
  },
];

const AUTO_ADVANCE_MS = 20000;

const DemoSlideshow = () => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => setIdx((i) => (i + 1) % EXAMPLES.length), []);
  const prev = useCallback(() => setIdx((i) => (i - 1 + EXAMPLES.length) % EXAMPLES.length), []);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(next, AUTO_ADVANCE_MS);
    return () => clearInterval(t);
  }, [paused, next]);

  const ex = EXAMPLES[idx];

  return (
    <div className="w-full lg:w-auto flex flex-col items-center gap-3" data-testid="demo-slideshow">
      <Card className="card-surface border-[#D4AF37]/30 bg-gradient-to-br from-[#1a0f2e] to-[#0A0A0A] w-full max-w-md">
        <CardContent className="p-5 sm:p-6 space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[#D4AF37]">
              <Sparkles className="w-4 h-4" />
              <span className="text-xs uppercase tracking-[0.2em] font-semibold">Real Examples</span>
            </div>
            <span className="text-xs text-[#71717A]" data-testid="slideshow-counter">
              {idx + 1} / {EXAMPLES.length}
            </span>
          </div>

          <p className="text-center text-xs text-[#A1A1AA]">
            Here&apos;s how 5 different personas respond. Auto-rotates — or tap to navigate.
          </p>

          {/* User message */}
          <div className="rounded-2xl border border-[#27272A] border-l-4 border-l-[#8B5CF6] bg-[#0A0A0A] p-4 min-h-[110px]">
            <p className="text-[10px] uppercase tracking-wider text-[#A1A1AA] mb-2" style={{ fontFamily: 'Outfit' }}>You</p>
            <p className="text-sm leading-relaxed text-[#F9FAFB]" data-testid="slideshow-user-text">{ex.user}</p>
          </div>

          {/* AI message */}
          <div className="rounded-2xl border border-[#27272A] bg-[#0A0A0A] p-4">
            <div className="flex items-center gap-2 mb-2">
              <Heart className="w-4 h-4 text-[#8B5CF6]" />
              <p className="text-[10px] uppercase tracking-wider text-[#A1A1AA]" style={{ fontFamily: 'Outfit' }} data-testid="slideshow-persona">{ex.persona}</p>
            </div>
            <p className="text-sm leading-relaxed text-[#F9FAFB] whitespace-pre-line" data-testid="slideshow-ai-text">{ex.ai}</p>

            <div className="mt-4 pt-3 border-t border-[#27272A]">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#A1A1AA] mb-2">Detected emotional tones</p>
              <div className="flex flex-wrap gap-1.5">
                {ex.tones.map((t) => (
                  <span key={t} className="text-[10px] px-2 py-1 rounded-full border border-[#27272A] bg-[#0A0A0A] text-[#A1A1AA]">{t}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={prev}
              className="w-10 h-10 rounded-full bg-[#0A0A0A] border border-[#27272A] flex items-center justify-center text-[#A1A1AA] hover:text-[#F9FAFB] hover:border-[#8B5CF6] transition-all"
              data-testid="slideshow-prev"
              aria-label="Previous example"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <button
              type="button"
              onClick={() => setPaused((p) => !p)}
              className="text-xs text-[#A1A1AA] hover:text-[#F9FAFB] flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#27272A] hover:border-[#8B5CF6] transition-all"
              data-testid="slideshow-toggle"
              aria-label={paused ? 'Resume slideshow' : 'Pause slideshow'}
            >
              {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
              {paused ? 'Play' : 'Pause'}
            </button>

            <button
              type="button"
              onClick={next}
              className="w-10 h-10 rounded-full bg-[#0A0A0A] border border-[#27272A] flex items-center justify-center text-[#A1A1AA] hover:text-[#F9FAFB] hover:border-[#8B5CF6] transition-all"
              data-testid="slideshow-next"
              aria-label="Next example"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-1.5" data-testid="slideshow-dots">
            {EXAMPLES.map((_, i) => (
              <button
                key={`dot-${EXAMPLES[i].persona}`}
                type="button"
                onClick={() => setIdx(i)}
                className={`w-2 h-2 rounded-full transition-all ${i === idx ? 'bg-[#D4AF37] w-6' : 'bg-[#27272A] hover:bg-[#71717A]'}`}
                aria-label={`Go to example ${i + 1}`}
                data-testid={`slideshow-dot-${i}`}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      <p className="text-[10px] text-[#71717A] italic text-center max-w-md px-4">
        Sample conversations. Real responses are shaped by your words, your tone, and the persona you choose.
      </p>
    </div>
  );
};

export default DemoSlideshow;
